import React, { useEffect, useState } from 'react';
import { clinicService } from '../../services/api';

const CopilotPolishPolicySection = ({ clinicId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [value, setValue] = useState('');
  const [defaultPrompt, setDefaultPrompt] = useState('');

  useEffect(() => {
    if (!clinicId) {
      setValue('');
      setDefaultPrompt('');
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        const data = await clinicService.getClinicCopilotSettings(clinicId);
        if (!cancelled) {
          setValue(data.copilot_rewrite_prompt || '');
          setDefaultPrompt(data.default_copilot_rewrite_prompt || '');
        }
      } catch (err) {
        console.error('Error loading copilot polish policy:', err);
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load polish policy');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [clinicId]);

  const savePrompt = async (prompt) => {
    if (!clinicId || saving) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const data = await clinicService.patchClinicCopilotSettings(clinicId, prompt);
      setValue(data.copilot_rewrite_prompt || '');
      if (data.default_copilot_rewrite_prompt) {
        setDefaultPrompt(data.default_copilot_rewrite_prompt);
      }
      setSuccess(
        prompt.trim()
          ? 'Improve polish policy saved for this clinic.'
          : 'Improve polish policy reset to the built-in default.'
      );
    } catch (err) {
      console.error('Error saving copilot polish policy:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save polish policy');
    } finally {
      setSaving(false);
    }
  };

  if (!clinicId) {
    return (
      <div className="bg-gray-50 p-3 md:p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Copilot</h3>
        <p className="text-sm text-gray-500">No clinic selected.</p>
      </div>
    );
  }

  const usingDefault = !value.trim();

  return (
    <div className="bg-gray-50 p-3 md:p-6 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-700 mb-2">Copilot</h3>
      <p className="text-sm text-gray-600 mb-4">
        Improve uses this clinic’s polish policy to rewrite the text staff typed. Leave empty to use
        the built-in rules. This does not change Copilot draft replies.
      </p>
      {loading && <p className="text-sm text-gray-500">Loading polish policy…</p>}
      {error && (
        <p className="mb-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="mb-2 text-sm text-green-700" role="status">
          {success}
        </p>
      )}
      {!loading && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700" htmlFor="copilot_rewrite_prompt">
            Improve polish policy
          </label>
          <textarea
            id="copilot_rewrite_prompt"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={saving}
            rows={12}
            placeholder={defaultPrompt}
            className="w-full p-2 border rounded font-mono text-sm"
          />
          {usingDefault && (
            <p className="text-xs text-gray-500">Empty policy uses the built-in Improve rules below.</p>
          )}
          {defaultPrompt && (
            <details className="rounded border border-gray-200 bg-white p-3">
              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                Built-in default policy
              </summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-600">{defaultPrompt}</pre>
            </details>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => savePrompt(value)}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => savePrompt('')}
              disabled={saving}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CopilotPolishPolicySection;
